/**
 * One column of the comparison grid: a locked KJV-over-NASB cell pair.
 * The NASB half is mirrored (number on top, gloss below) so the Strong's
 * numbers face each other across the row boundary. Ghost cells mark
 * where one text has nothing at this slot.
 */

import { Fragment } from 'react';
import { bibleState } from '@/state/bible-state';
import type { BibleLocation } from '@skjv/common';
import type { AlignedSlot } from './alignment';

interface WordCellColumnProps {
  slot: AlignedSlot;
  glosses: Map<string, string>;
  location: BibleLocation;
}

interface WordCellGroupProps {
  slots: AlignedSlot[];
  glosses: Map<string, string>;
  location: BibleLocation;
}

const STATUS_CLASSES: Record<AlignedSlot['status'], string> = {
  common: '',
  'kjv-only': 'token-kjv-only',
  'nasb-only': 'token-nasb-only',
};

/**
 * A group is one English word over its several Strong's numbers. Slots
 * inside a group always have a KJV half (a KJV-less slot breaks the run
 * upstream), so the status here is only ever common or kjv-only.
 */
export function WordCellGroup({ slots, glosses, location }: WordCellGroupProps) {
  if (slots.length === 1) {
    return <WordCellColumn slot={slots[0]} glosses={glosses} location={location} />;
  }

  const text = slots[0].kjv?.text ?? '';
  const isWhollyKjvOnly = slots.every((s) => s.status === 'kjv-only');

  return (
    <div className={`analysis-token-col ${isWhollyKjvOnly ? STATUS_CLASSES['kjv-only'] : ''}`}>
      <div className="analysis-cell analysis-cell-kjv">
        <span className="analysis-cell-word">{text}</span>
        <span className="analysis-cell-numbers">
          {slots.map((slot, i) => (
            <Fragment key={`${slot.kjv?.number}-${i}`}>
              {i > 0 && <span className="analysis-number-join">+</span>}
              <button
                className={`analysis-cell-number analysis-number-chip ${
                  !isWhollyKjvOnly && slot.status === 'kjv-only' ? 'analysis-number-kjv-only' : ''
                }`}
                onClick={() =>
                  bibleState.showStrongsDefinition(slot.kjv!.number, location, {
                    englishWord: text,
                  })
                }
              >
                {slot.kjv!.number}
              </button>
            </Fragment>
          ))}
        </span>
      </div>

      <div className="analysis-group-nasb">
        {slots.map((slot, i) => (
          <NasbCell key={i} slot={slot} glosses={glosses} location={location} />
        ))}
      </div>
    </div>
  );
}

function NasbCell({ slot, glosses, location }: WordCellColumnProps) {
  if (!slot.nasb) {
    return (
      <div className="analysis-cell analysis-cell-nasb analysis-cell-ghost" aria-hidden="true">
        <span className="analysis-cell-ghost-mark">∅</span>
      </div>
    );
  }
  return (
    <button
      className="analysis-cell analysis-cell-nasb"
      onClick={() => bibleState.showStrongsDefinition(slot.nasb!.number, location)}
    >
      <span className="analysis-cell-number">{slot.nasb.number}</span>
      <span className="analysis-cell-gloss">{glosses.get(slot.nasb.number) ?? ''}</span>
    </button>
  );
}

export function WordCellColumn({ slot, glosses, location }: WordCellColumnProps) {
  const openStrongs = (number: string, englishWord?: string) => {
    bibleState.showStrongsDefinition(number, location, englishWord ? { englishWord } : undefined);
  };

  return (
    <div className={`analysis-token-col ${STATUS_CLASSES[slot.status]}`}>
      {slot.kjv ? (
        <button
          className="analysis-cell analysis-cell-kjv"
          onClick={() => openStrongs(slot.kjv!.number, slot.kjv!.text)}
        >
          <span className="analysis-cell-word">{slot.kjv.text}</span>
          <span className="analysis-cell-number">{slot.kjv.number}</span>
        </button>
      ) : (
        <div className="analysis-cell analysis-cell-kjv analysis-cell-ghost" aria-hidden="true">
          <span className="analysis-cell-ghost-mark">∅</span>
        </div>
      )}

      <NasbCell slot={slot} glosses={glosses} location={location} />
    </div>
  );
}
